import moment from 'moment'
import { Component, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { AssessmentDto } from 'src/app/models/flip/assessment-dto'
import { AssessmentGroupDto } from 'src/app/models/flip/assessment-group-dto'
import { BannerService } from 'src/app/services/banner/banner.service'
import { ClientService } from 'src/app/services/client/client.service'
import { FlipService } from 'src/app/services/flip/flip.service'
import { PreviousRouteService } from 'src/app/services/previous-route/previous-route.service'
import { CommonService } from 'src/app/services/common/common.service'
import { ClientInformationAttribute } from 'src/app/models/client-information/ClientInformationAttribute'
import { ClientDto } from '../../../models/client/client-dto'

@Component({
  selector: 'app-flip-instance',
  templateUrl: './flip-instance.component.html',
  styleUrls: ['./flip-instance.component.scss'],
  standalone: false,
})
export class FlipInstanceComponent implements OnInit {
  _clientName: string = 'Loading...'
  _clientId: string
  _clientDto: ClientDto
  data_collapsible: any[]
  last_assessment: string
  age: string = ''
  dob: Date
  assessmentStatus: string = 'This assessment is locked'
  //Client Attributes
  _clientInformationAttributes: ClientInformationAttribute[] =
    this.commonService.createEmptyAttributeList()
  _clientInformationTitle = 'Client Information'
  _clientInformationDisplayText = ''
  _clientInformationUrl = ''
  _clientInFocus: string = ''
  _clientEditEnabled: boolean = false //Perform check based on Feature Flag
  _totalReviews = 0
  _hasAccess: any = {
    slatHasAccess: false,
    flipHasAccess: false,
    reddiHasAccess: false,
  }

  constructor(
    private router: ActivatedRoute,
    private clientService: ClientService,
    private bannerService: BannerService,
    private flipservice: FlipService,
    protected previousRouteService: PreviousRouteService,
    private readonly commonService: CommonService,
  ) {}

  ngOnInit(): void {
    this.router.paramMap.subscribe((paramMap) => {
      const clientId = paramMap.get('clientId')
      if (!this.commonService.isValidUUID(clientId)) {
        this.bannerService.showBanner(
          `Invalid clientId ${clientId}.`,
          'error',
          30000,
        )
        throw new Error('Invalid ClientId')
      }
      this.clientService.Details(clientId).subscribe({
        next: (clientDetails) => {
          this._clientId = clientId
          this._clientName = clientDetails.preferredFullName
          this.age = clientDetails.age
          this.dob = clientDetails.dateOfBirth
          this._clientDto = clientDetails
          this.setClientInformation(clientDetails)

          //tools has access check
          this.checkToolsAccess()
          this.retrieveIndicators(clientDetails)
        },
        error: (err) => {
          this.bannerService.showBanner(
            'An error occured while retrieving the client details.',
            'error',
            300000,
          )
          throw err
        },
      })

      const instanceId = paramMap.get('instanceId')

      this.flipservice
        .GetAssessmentInstanceById(instanceId)
        .subscribe({
          next: (assessmentDto: AssessmentDto) => {
            this.data_collapsible =
              this.mapFlipInstanceToCollapsible(assessmentDto)
            if (assessmentDto.lastAssessmentDate) {
              const date = new Date(assessmentDto.lastAssessmentDate)
              const formattedDate = `${date.getDate()} ${date.toLocaleString(
                'default',
                { month: 'short' },
              )}, ${date.getFullYear()}`
              this.last_assessment = 'Assessment date: ' + formattedDate
            } else {
              this.last_assessment = ''
            }
          },
          error: (err) => {
            this.bannerService.showBanner(
              'An error occured while retrieving the fliInstanceItem details.',
              'error',
              300000,
            )
            console.log(err)
            throw err
          },
        })
    })
  }

  routerLinkClicked() {
    const prevUrl = this.previousRouteService.getPreviousUrl()
    if (prevUrl) {
      this.previousRouteService.goToPreviousUrl(prevUrl)
    }
  }

  navigateTo(target: string): void {
    this.commonService.navigateTo(target, this._clientId)
  }

  setClientInformation(client: ClientDto) {
    this._clientInFocus = client.contactId
    this._clientInformationDisplayText = this._clientEditEnabled
      ? 'Edit profile'
      : ''
    this._clientInformationUrl = this._clientEditEnabled
      ? '/edit-client/' + client.contactId
      : ''

    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Age',
      client.age,
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'DOB',
      moment(client.dateOfBirth).format('Do MMMM, YYYY'),
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Gender',
      client.genderCodeName,
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Case Manager',
      client.clientCaseManagerName,
    )
  }

  checkToolsAccess() {
    this._hasAccess.flipHasAccess = this.commonService.clientHasAccessToFlip(
      this._clientDto,
    )
    this._hasAccess.reddiHasAccess = this.commonService.clientHasAccessToReddi(
      this._clientDto,
    )
    this._hasAccess.slatHasAccess = this.commonService.clientHasAccessToSlat(
      this._clientDto,
    )
  }

  retrieveIndicators(client: ClientDto) {
    this._totalReviews = this.commonService.returnReviewIndicator(client)
  }

  // Map the return data to item
  private mapFlipInstanceToCollapsible(assessmentDto: AssessmentDto): any[] {
    let groupIndex = 0
    const groupedByGroupName = assessmentDto.groups?.reduce(
      (acc: { [key: string]: any }, group: AssessmentGroupDto) => {
        groupIndex++
        const groupName = group.name || ''
        if (!acc[groupName]) {
          acc[groupName] = {
            header: groupName,
            last_assessment: assessmentDto.assessmentDate,
            subItems: [],
          }
        }
        // Sort items within each group by orderNumber (assuming orderNumber is a number)
        group.items.sort((a, b) => {
          return a.orderNumber - b.orderNumber
        })
        let subOrder = 0
        group.items.forEach((item) => {
          subOrder++
          acc[groupName].subItems.push({
            number: `Item ${groupIndex}.${subOrder}`,
            content: item.name,
            score: this.getScore(item.score),
          })
        })

        // Sort subItems within each group by orderNumber
        acc[groupName].subItems.sort(
          (a: { number: string }, b: { number: any }) => {
            return a.number.localeCompare(b.number, undefined, {
              numeric: true,
              sensitivity: 'base',
            })
          },
        )

        return acc
      },
      {},
    )

    return groupedByGroupName ? Object.values(groupedByGroupName) : []
  }

  // Function to determine the score text based on the score value
  private getScore(score: boolean | null): string {
    if (score == null) {
      return ''
    }
    return score ? 'Mostly' : 'Rarely'
  }
}
